/**
 * An app's settings, as a capability its module can hold (RFC 0284 S6g).
 *
 * NOT A CONTEXT, unlike `window`, `docs` and `sessions`. Those are the window's
 * and every fill in it sees the same ones. Settings belong to ONE app,
 * so the thing that mounts it is the thing that knows which, and a context
 * would have to be re-provided per mount to mean anything.
 *
 * The same ref-and-effect shape `Thread` uses for its session, and for
 * the same reason: a module is handed the object once and keeps it, so `get`
 * has to read through a getter rather than close over a snapshot.
 */

import { appSettings, type SettingValue } from '@mod/core/model'
import { useEffect, useMemo, useRef } from 'react'
import { makeSettings, type SettingsCapability } from './settings'

/** What this needs of a catalog entry: the spec and the values, nothing else. */
export interface SettingsSource {
  settings?: { key: string; type: 'string' | 'number' | 'boolean' }[]
  settingValues?: Record<string, SettingValue>
}

export function useAppSettings(app: SettingsSource | undefined) {
  // Merged here rather than in the capability, so the identity of this object
  // is the signal for notifying subscribers below.
  const spec = app?.settings
  const stored = app?.settingValues
  // THE TWO FIELDS, not the app. An entry is rebuilt from the catalog on every
  // sync tick that touches anything, so depending on the app itself would make
  // a fill redraw constantly for settings that never changed.
  const values = useMemo(
    () => appSettings({ settings: spec, settingValues: stored } as never),
    [spec, stored],
  )

  const valuesRef = useRef(values)
  valuesRef.current = values
  const subscribers = useRef(new Set<() => void>())

  // `values` is the SIGNAL, not a value the body reads.
  // biome-ignore lint/correctness/useExhaustiveDependencies: deliberate signal dependency
  useEffect(() => {
    for (const fn of subscribers.current) fn()
  }, [values])

  return useMemo<SettingsCapability>(
    () =>
      makeSettings({
        values: () => valuesRef.current,
        subscribe: (fn) => {
          subscribers.current.add(fn)
          return () => {
            subscribers.current.delete(fn)
          }
        },
      }),
    [],
  )
}
